import { useMemo, useState, type MouseEvent } from 'react';
import { Alert, Box, Menu, MenuItem, Typography } from '@mui/material';
import ManIcon from '@mui/icons-material/Man';
import WomanIcon from '@mui/icons-material/Woman';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import LockIcon from '@mui/icons-material/Lock';
import type { BusSeat } from '../../../api/types/bus';

type Gender = 'E' | 'K';

/** `availableGenders` → the gender codes the provider allows on this seat (the aisle-mate
 *  rule is already applied). "Erkek" → 'E', "Bayan"/"Kadın" → 'K'. Empty ⇒ both. */
function allowedGenders(seat: BusSeat): Gender[] {
  const raw = seat.availableGenders ?? [];
  const out = new Set<Gender>();
  for (const g of raw) {
    const t = g.toLocaleLowerCase('tr');
    if (t.startsWith('erkek') || t === 'male' || t === 'e') out.add('E');
    if (t.startsWith('bayan') || t.startsWith('kad') || t === 'female' || t === 'k') out.add('K');
  }
  return out.size ? [...out] : ['E', 'K'];
}

function occupantGender(seat: BusSeat): Gender | null {
  const g = (seat.gender ?? '').toLocaleLowerCase('tr');
  if (g.startsWith('erkek') || g === 'male') return 'E';
  if (g.startsWith('bayan') || g.startsWith('kad') || g === 'female') return 'K';
  return null;
}

const seatKey = (s: BusSeat): string | number => s.seatNum ?? s.seatNumber ?? s.seatNo ?? '';

/**
 * Bus seat map — renders `Bus/GetBusSchema` `seats[]` as a layout using each seat's
 * `rowNum`/`colNum`/`floorNum`. Taken seats are coloured by occupant gender (blue = erkek,
 * pink = kadın); picking a free seat opens an Erkek/Kadın menu constrained to that seat's
 * `availableGenders` (the yan-yana / aisle-mate rule is enforced by the provider inside
 * that list). Falls back to the provider `busSchemaHtml` (display-only) or manual entry
 * when no structured `seats` come back.
 */
export function SeatPicker({
  seats,
  informationMessage,
  busSchemaHtml,
  selectedSeats,
  onSelect,
  onRemove,
  maxSeats = 4,
}: {
  seats: BusSeat[] | null | undefined;
  informationMessage?: string | null;
  busSchemaHtml?: string | null;
  selectedSeats: BusSeat[];
  onSelect: (seat: BusSeat, gender: Gender) => void;
  onRemove: (seat: BusSeat) => void;
  maxSeats?: number;
}) {
  // Menü tıklanan koltuğun DOM'una değil, imleç konumuna açılır — her seçimde grid yeniden
  // render olup hücre elemanları değiştiği için `anchorEl` kopuyor ve menü sol-üste düşüyordu.
  const [menu, setMenu] = useState<{ top: number; left: number; seat: BusSeat } | null>(null);

  const selectedKeys = useMemo(
    () => new Set(selectedSeats.map(seatKey)),
    [selectedSeats]
  );

  const floors = useMemo(() => {
    const list = (seats ?? []).filter((s) => s && (s.rowNum != null || s.colNum != null));
    const byFloor = new Map<number, BusSeat[]>();
    for (const s of list) {
      const f = s.floorNum ?? 0;
      byFloor.set(f, [...(byFloor.get(f) ?? []), s]);
    }
    return [...byFloor.entries()].sort((a, b) => a[0] - b[0]);
  }, [seats]);

  const hasGrid = floors.length > 0;

  /** Yan koltuk (aynı sıra, komşu sütun) bir cinsiyetle seçildiyse bu koltuk da yalnız o
   *  cinsiyetle seçilebilir — "yan yana farklı cinsiyet alınamaz" kuralı. Sağlayıcının
   *  `availableGenders`'ı zaten DOLU komşuları kapsıyor; bu, kullanıcının kendi çoklu
   *  seçimini de tutarlı tutar. */
  const neighbourGender = (seat: BusSeat): Gender | null => {
    const r = seat.rowNum;
    const c = seat.colNum;
    if (r == null || c == null) return null;
    const n = selectedSeats.find(
      (s) => s.rowNum === r && s.colNum != null && Math.abs(s.colNum - c) === 1
    );
    return n?.selectedGender ?? null;
  };

  const genderChoicesFor = (seat: BusSeat): Gender[] => {
    const forced = neighbourGender(seat);
    return allowedGenders(seat).filter((g) => !forced || g === forced);
  };

  const openMenu = (e: MouseEvent<HTMLElement>, seat: BusSeat) => {
    if (selectedKeys.has(seatKey(seat))) {
      onRemove(seat);
      return;
    }
    if (selectedSeats.length >= maxSeats) return;
    const genders = genderChoicesFor(seat);
    if (genders.length === 0) return;
    if (genders.length === 1) {
      onSelect(seat, genders[0]);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({ top: rect.bottom, left: rect.left + rect.width / 2, seat });
  };

  const renderFloor = ([floorNum, floorSeats]: [number, BusSeat[]]) => {
    const rows = floorSeats.map((s) => s.rowNum ?? 0);
    const cols = floorSeats.map((s) => s.colNum ?? 0);
    const minRow = Math.min(...rows);
    const maxRow = Math.max(...rows);
    const minCol = Math.min(...cols);
    const maxCol = Math.max(...cols);
    const at = (r: number, c: number) =>
      floorSeats.find((s) => (s.rowNum ?? 0) === r && (s.colNum ?? 0) === c);

    // Bus drawn lengthwise: each column (lane) is a visual row, top = highest colNum;
    // the lowest colNum lane sits next to the driver.
    const laneRows: number[] = [];
    for (let c = maxCol; c >= minCol; c--) laneRows.push(c);
    const positions: number[] = [];
    for (let r = minRow; r <= maxRow; r++) positions.push(r);

    return (
      <Box key={floorNum} sx={{ mb: floors.length > 1 ? 3 : 0 }}>
        {floors.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            {floorNum === 0 ? 'Alt kat' : `${floorNum}. kat`}
          </Typography>
        )}
        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 0.75,
            p: 1.5,
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
            overflowX: 'auto',
            maxWidth: '100%',
          }}
        >
          {laneRows.map((c, laneIdx) => {
            const isLast = laneIdx === laneRows.length - 1;
            return (
              <Box
                key={c}
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                  // 2+1 düzeninde tek koltuk sırasından önce koridor boşluğu.
                  mt: isLast && laneRows.length >= 3 ? 1.75 : 0,
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    fontSize: 10,
                    color: 'text.disabled',
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isLast ? '🚍' : laneIdx === 0 ? 'Kapı' : ''}
                </Box>
                {positions.map((r) => {
                  const seat = at(r, c);
                  return <Cell key={r} seat={seat} />;
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    );

    function Cell({ seat }: { seat: BusSeat | undefined }) {
      if (!seat) return <Box sx={{ width: 34, height: 34, flexShrink: 0 }} />;
      const num = seat.seatNum ?? seat.seatNumber ?? 0;
      if (!num || num <= 0) {
        const t = (seat.seatType ?? '').toLocaleUpperCase('tr');
        const label = t === 'KAPI' ? 'K' : t === 'MASA' ? 'D' : '';
        return (
          <Box
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              color: 'text.disabled',
              bgcolor: label ? 'action.hover' : 'transparent',
              borderRadius: 1,
            }}
          >
            {label}
          </Box>
        );
      }

      const isSelected = selectedKeys.has(seatKey(seat));
      const selSeat = selectedSeats.find((s) => seatKey(s) === seatKey(seat));
      const occ = occupantGender(seat);
      const sold = seat.isSold === true;
      const disabled = sold || (!isSelected && selectedSeats.length >= maxSeats);

      let bg = 'transparent';
      let border = 'divider';
      let icon = <AirlineSeatReclineNormalIcon sx={{ fontSize: 18, color: 'text.secondary' }} />;
      if (isSelected) {
        bg = 'success.light';
        border = 'success.main';
        icon =
          selSeat?.selectedGender === 'K' ? (
            <WomanIcon sx={{ fontSize: 20, color: 'success.dark' }} />
          ) : (
            <ManIcon sx={{ fontSize: 20, color: 'success.dark' }} />
          );
      } else if (sold && occ === 'E') {
        bg = 'info.light';
        border = 'info.main';
        icon = <ManIcon sx={{ fontSize: 20, color: 'info.dark' }} />;
      } else if (sold && occ === 'K') {
        bg = '#f8d7da';
        border = '#e39aa2';
        icon = <WomanIcon sx={{ fontSize: 20, color: '#a03e4a' }} />;
      } else if (sold) {
        bg = 'action.disabledBackground';
        icon = <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />;
      }

      return (
        <Box
          role="button"
          aria-label={`Koltuk ${num}${sold ? ' (dolu)' : ''}`}
          aria-disabled={disabled}
          onClick={(e) => !disabled && openMenu(e, seat)}
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1.5px solid',
            borderColor: border,
            borderRadius: 1,
            bgcolor: bg,
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled && !sold ? 0.5 : 1,
            lineHeight: 1,
            '&:hover': disabled ? undefined : { borderColor: 'primary.main' },
          }}
        >
          {icon}
          <Box component="span" sx={{ fontSize: 10, color: 'text.secondary' }}>
            {num}
          </Box>
        </Box>
      );
    }
  };

  if (hasGrid) {
    return (
      <Box>
        {informationMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {informationMessage}
          </Alert>
        )}
        {floors.map(renderFloor)}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
          <Legend swatch="transparent" border label="Boş" />
          <Legend swatch="info.light" label="Dolu — Erkek" icon={<ManIcon sx={{ fontSize: 16 }} />} />
          <Legend swatch="#f8d7da" label="Dolu — Kadın" icon={<WomanIcon sx={{ fontSize: 16 }} />} />
          <Legend swatch="success.light" label="Seçtiğiniz" />
        </Box>

        <Menu
          open={!!menu}
          onClose={() => setMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={menu ? { top: menu.top, left: menu.left } : undefined}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          {menu &&
            genderChoicesFor(menu.seat).map((g) => (
              <MenuItem
                key={g}
                onClick={() => {
                  onSelect(menu.seat, g);
                  setMenu(null);
                }}
              >
                {g === 'E' ? (
                  <ManIcon sx={{ fontSize: 18, mr: 1 }} />
                ) : (
                  <WomanIcon sx={{ fontSize: 18, mr: 1 }} />
                )}
                {g === 'E' ? 'Erkek' : 'Kadın'}
              </MenuItem>
            ))}
        </Menu>
      </Box>
    );
  }

  if (busSchemaHtml) {
    return (
      <Box>
        {informationMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {informationMessage}
          </Alert>
        )}
        <Box
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2, overflowX: 'auto' }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: busSchemaHtml }}
        />
        <Typography variant="body2" color="text.secondary">
          Koltuk düzeni API'den hazır HTML olarak geldi — seçim için koltuk numarasını girip ekleyin.
        </Typography>
      </Box>
    );
  }

  return (
    <Alert severity="warning">Bu sefer için koltuk düzeni bulunamadı — koltuk numarasını elle girin.</Alert>
  );
}

function Legend({
  swatch,
  label,
  border,
  icon,
}: {
  swatch: string;
  label: string;
  border?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: 0.5,
          bgcolor: swatch,
          border: border ? '1.5px solid' : 'none',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}
